import React, { useState, useEffect, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
         ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import { fetchUserStats, fetchUsersList, updateUser, deleteUser } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { SERVER_BASE_URL } from '../config';

// Colors for charts and indicators
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];
const STATUS_COLORS = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-gray-100 text-gray-800',
  expired: 'bg-red-100 text-red-800',
  pending: 'bg-yellow-100 text-yellow-800'
};

// Region data placeholder (temporary)
const REGION_DATA = [
  { name: 'Moscow', value: 35 },
  { name: 'Saint Petersburg', value: 25 },
  { name: 'Ekaterinburg', value: 15 },
  { name: 'Novosibirsk', value: 10 },
  { name: 'Other', value: 15 }
];

const UsersTab = () => {
  // Component state
  const [userData, setUserData] = useState({
    totalUsers: 0,
    activeSubscriptions: 0,
    bySubscriptionType: {},
    byRole: {},
    recentRegistrations: [],
    monthlyGrowth: [], // New state for growth data
    events_by_user: {} // Added to store events by user data
  });
  const [userList, setUserList] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [error, setError] = useState(null);
  const [, setSelectedUser] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isTrialModalOpen, setIsTrialModalOpen] = useState(false); // NEW
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false); // NEW
  const [isExporting, setIsExporting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userForTrial, setUserForTrial] = useState(null); // NEW
  const [userForSubscription, setUserForSubscription] = useState(null); // NEW
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    subscriptionStatus: '',
    subscriptionType: '',
    dateRange: 'all'
  });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  // Pagination
  const [pagination, setPagination] = useState({
    skip: 0,
    limit: 10,
    total: 0
  });
  
  // Visible columns settings
  const [visibleColumns, setVisibleColumns] = useState({
    id: true,
    name: true,
    email: true,
    role: true,
    subscriptionStatus: true,
    subscriptionType: true,
    subscriptionEnd: true,
    trialStatus: true, // NEW
    createdAt: false,
    lastLogin: true,
    eventsCount: true
  });
  const [isColumnSettingsOpen, setIsColumnSettingsOpen] = useState(false);
  
  // User edit form
  const [editForm, setEditForm] = useState({
    id: '',
    name: '',
    email: '',
    role: '',
    subscriptionStatus: '',
    subscriptionType: '',
    subscriptionEnd: ''
  });

  // NEW: Trial form
  const [trialForm, setTrialForm] = useState({
    days: 14
  });

  // NEW: Subscription form
  const [subscriptionForm, setSubscriptionForm] = useState({
    days: 30,
    subscriptionType: 'basic',
    endDate: ''
  });

  // Get auth data from context
  const { isAuthenticated, user } = useAuth();
  
  // Get token from localStorage
  const token = localStorage.getItem('token');
  
  // Refs for modal windows
  const editModalRef = useRef(null);
  const deleteModalRef = useRef(null);
  const trialModalRef = useRef(null); // NEW
  const subscriptionModalRef = useRef(null); // NEW
  const columnSettingsRef = useRef(null);

  // NEW: API functions for trial and subscription management
  const activateUserTrial = async (userId, days) => {
    const response = await fetch(`${SERVER_BASE_URL}/api/admin/users/${userId}/trial?days=${days}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return response;
  };

  const activateUserSubscription = async (userId, subscriptionData) => {
    const response = await fetch(`${SERVER_BASE_URL}/api/admin/users/${userId}/subscription`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(subscriptionData)
    });
    return response;
  };

  const cancelUserSubscription = async (userId) => {
    const response = await fetch(`${SERVER_BASE_URL}/api/admin/users/${userId}/subscription`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return response;
  };

  const cancelUserTrial = async (userId) => {
    const response = await fetch(`${SERVER_BASE_URL}/api/admin/users/${userId}/trial`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return response;
  };

  // Check if user has admin rights
  useEffect(() => {
    if (isAuthenticated && user && user.role !== 'admin') {
      setError('You do not have permission to access the admin panel');
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 300);

    return () => clearTimeout(timer);
  }, [filters.search]);

  // Load user statistics
  useEffect(() => {
    async function loadUserStats() {
      try {
        setStatsLoading(true);
        const response = await fetchUserStats(token);
        
        if (!response.ok) {
          throw new Error(`Error loading statistics: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Prepare monthly growth data for chart
        const monthlyGrowth = prepareMonthlyGrowthData(data.registrations_by_month || {});
        
        setUserData({
          totalUsers: data.total_users,
          activeSubscriptions: data.active_subscriptions,
          bySubscriptionType: data.by_subscription_type,
          byRole: data.by_role,
          recentRegistrations: data.recent_registrations,
          monthlyGrowth: monthlyGrowth,
          events_by_user: data.events_by_user || {} // Store events by user data
        });
        
        // Set total users count for pagination
        setPagination(prev => ({...prev, total: data.total_users}));
      } catch (err) {
        console.error('Error loading user statistics:', err);
        setError(err.message);
      } finally {
        setStatsLoading(false);
      }
    }
    
    if (token && isAuthenticated && user?.role === 'admin') {
      loadUserStats();
    }
  }, [token, isAuthenticated, user]);

  // Function to prepare monthly growth data
  const prepareMonthlyGrowthData = (monthlyData) => {
    // Months array for sorting
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // If no data, return empty array
    if (!monthlyData || typeof monthlyData !== 'object' || Object.keys(monthlyData).length === 0) {
      return [];
    }
    
    // Create array of objects for chart
    let growthData = Object.entries(monthlyData).map(([key, value]) => {
      return {
        month: key, // Month name directly from API
        count: value
      };
    });
    
    // Sort by month order (if needed)
    growthData.sort((a, b) => {
      const monthAIndex = months.indexOf(a.month);
      const monthBIndex = months.indexOf(b.month);
      return monthAIndex - monthBIndex;
    });
    
    return growthData;
  };

  // Load users list with filtering
  useEffect(() => {
    async function loadUsers() {
      try {
        setUsersLoading(true);
        
        // Form request parameters based on filters
        const params = {
          token,
          skip: pagination.skip,
          limit: pagination.limit,
          subscriptionType: filters.subscriptionType,
          role: filters.role,
          subscriptionStatus: filters.subscriptionStatus,
          search: debouncedSearch,
          dateRange: filters.dateRange
        };
        
        const response = await fetchUsersList(params);
        
        if (!response.ok) {
          throw new Error(`Error loading users: ${response.status}`);
        }
        
        const data = await response.json();
        setUserList(data);
        
        // Assume response has total count header
        const totalCount = response.headers.get('X-Total-Count');
        if (totalCount) {
          const parsedTotal = parseInt(totalCount, 10);
          setPagination((prev) => ({ ...prev, total: Number.isNaN(parsedTotal) ? prev.total : parsedTotal }));
        }
      } catch (err) {
        console.error('Error loading users list:', err);
        setError(err.message);
      } finally {
        setUsersLoading(false);
      }
    }
    
    if (token && isAuthenticated && user?.role === 'admin') {
      loadUsers();
    }
  }, [
    token, 
    isAuthenticated, 
    user, 
    pagination.skip, 
    pagination.limit, 
    filters.subscriptionType,
    filters.role,
    filters.subscriptionStatus,
    debouncedSearch,
    filters.dateRange
  ]);

  // Event handlers
  
  // Handle page change for pagination
  const handlePageChange = (newSkip) => {
    setPagination(prev => ({...prev, skip: newSkip}));
  };
  
  // Handle filter change
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({...prev, [filterName]: value}));
    // Reset pagination when filters change
    setPagination(prev => ({...prev, skip: 0}));
  };
  
  // Open edit modal
  const handleEditClick = (user) => {
    setSelectedUser(user);
    setEditForm({
      id: user.id,
      name: user.name || '',
      email: user.email || '',
      role: user.role || '',
      subscriptionStatus: user.subscription_status || '',
      subscriptionType: user.subscription_type || '',
      subscriptionEnd: user.subscription_end ? new Date(user.subscription_end).toISOString().split('T')[0] : ''
    });
    setIsEditModalOpen(true);
  };
  
  // Close edit modal
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedUser(null);
  };
  
  // Open delete modal
  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setIsDeleteModalOpen(true);
  };
  
  // Close delete modal
  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setUserToDelete(null);
  };

  // NEW: Open trial modal
  const handleTrialClick = (user) => {
    setUserForTrial(user);
    setTrialForm({ days: 14 });
    setIsTrialModalOpen(true);
  };

  // NEW: Close trial modal
  const handleCloseTrialModal = () => {
    setIsTrialModalOpen(false);
    setUserForTrial(null);
  };

  // NEW: Open subscription modal
  const handleSubscriptionClick = (user) => {
    setUserForSubscription(user);
    setSubscriptionForm({
      days: 30,
      subscriptionType: 'basic',
      endDate: ''
    });
    setIsSubscriptionModalOpen(true);
  };

  // NEW: Close subscription modal
  const handleCloseSubscriptionModal = () => {
    setIsSubscriptionModalOpen(false);
    setUserForSubscription(null);
  };
  
  // Handle edit form change
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm({...editForm, [name]: value});
  };

  // NEW: Handle trial form change
  const handleTrialFormChange = (e) => {
    const { name, value } = e.target;
    setTrialForm({...trialForm, [name]: parseInt(value)});
  };

  // NEW: Handle subscription form change
  const handleSubscriptionFormChange = (e) => {
    const { name, value } = e.target;
    setSubscriptionForm({...subscriptionForm, [name]: value});
  };
  
  // Save user changes
  const handleSaveUser = async () => {
    try {
      setIsSaving(true);
      
      // Transform form data for API
      const userData = {
        id: editForm.id,
        name: editForm.name,
        email: editForm.email,
        role: editForm.role,
        subscription_status: editForm.subscriptionStatus,
        subscription_type: editForm.subscriptionType,
        subscription_end: editForm.subscriptionEnd || null
      };
      
      // Send update request
      const response = await updateUser(token, userData);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Error updating user: ${response.status}`);
      }
      
      const updatedUser = await response.json();
      
      // Update users list
      const updatedList = userList.map(u => 
        u.id === updatedUser.id ? updatedUser : u
      );
      
      setUserList(updatedList);
      handleCloseEditModal();
      
      // Show success notification
      alert('User successfully updated');
      setIsSaving(false);
    } catch (err) {
      console.error('Error saving user:', err);
      setError(err.message);
      setIsSaving(false);
      alert(`Error: ${err.message}`);
    }
  };

  // NEW: Activate trial
  const handleActivateTrial = async () => {
    try {
      setIsSaving(true);
      
      const response = await activateUserTrial(userForTrial.id, trialForm.days);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Error activating trial: ${response.status}`);
      }
      
      const result = await response.json();
      
      // Update user in list
      const updatedList = userList.map(u => 
        u.id === userForTrial.id ? {...u, trial_ends_at: result.trial_ends_at, trial_started_at: result.trial_started_at} : u
      );
      setUserList(updatedList);
      
      handleCloseTrialModal();
      alert('Trial successfully activated');
      setIsSaving(false);
    } catch (err) {
      console.error('Error activating trial:', err);
      setError(err.message);
      setIsSaving(false);
      alert(`Error: ${err.message}`);
    }
  };

  // NEW: Activate subscription
  const handleActivateSubscription = async () => {
    try {
      setIsSaving(true);
      
      const subscriptionData = {
        subscription_type: subscriptionForm.subscriptionType,
        ...(subscriptionForm.endDate ? 
          { end_date: subscriptionForm.endDate } : 
          { days: parseInt(subscriptionForm.days) })
      };
      
      const response = await activateUserSubscription(userForSubscription.id, subscriptionData);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Error activating subscription: ${response.status}`);
      }
      
      const result = await response.json();
      
      // Update user in list
      const updatedList = userList.map(u => 
        u.id === userForSubscription.id ? {
          ...u, 
          subscription_status: result.subscription_status,
          subscription_type: result.subscription_type,
          subscription_end: result.subscription_end
        } : u
      );
      setUserList(updatedList);
      
      handleCloseSubscriptionModal();
      alert('Subscription successfully activated');
      setIsSaving(false);
    } catch (err) {
      console.error('Error activating subscription:', err);
      setError(err.message);
      setIsSaving(false);
      alert(`Error: ${err.message}`);
    }
  };

  // NEW: Cancel subscription and trial
  const handleCancelSubscription = async (userId) => {
    if (!confirm('Are you sure you want to cancel subscription and trial for this user?')) {
      return;
    }

    try {
      const response = await cancelUserSubscription(userId);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Error cancelling subscription: ${response.status}`);
      }
      
      // Update user in list
      const updatedList = userList.map(u => 
        u.id === userId ? {
          ...u, 
          subscription_status: 'inactive',
          subscription_type: null,
          subscription_end: null,
          trial_started_at: null,
          trial_ends_at: null
        } : u
      );
      setUserList(updatedList);
      
      alert('Subscription and trial cancelled');
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      setError(err.message);
      alert(`Error: ${err.message}`);
    }
  };

  // NEW: Cancel only trial
  const handleCancelTrial = async (userId) => {
    if (!confirm('Are you sure you want to cancel trial for this user?')) {
      return;
    }

    try {
      const response = await cancelUserTrial(userId);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Error cancelling trial: ${response.status}`);
      }
      
      // Update user in list
      const updatedList = userList.map(u => 
        u.id === userId ? {
          ...u, 
          trial_started_at: null,
          trial_ends_at: null
        } : u
      );
      setUserList(updatedList);
      
      alert('Trial cancelled');
    } catch (err) {
      console.error('Error cancelling trial:', err);
      setError(err.message);
      alert(`Error: ${err.message}`);
    }
  };
  
  // Delete user
  const handleConfirmDelete = async () => {
    try {
      // Send delete request
      const response = await deleteUser(token, userToDelete.id);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Error deleting user: ${response.status}`);
      }
      
      // Update users list
      const updatedList = userList.filter(u => u.id !== userToDelete.id);
      setUserList(updatedList);
      
      // Update total statistics
      setUserData({
        ...userData,
        totalUsers: userData.totalUsers - 1,
        activeSubscriptions: userToDelete.subscription_status === 'active' 
          ? userData.activeSubscriptions - 1 
          : userData.activeSubscriptions
      });
      
      handleCloseDeleteModal();
      
      // Show success notification
      alert('User successfully deleted');
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err.message);
      alert(`Error: ${err.message}`);
    }
  };
  
  // Export users list to CSV
  const handleExportCSV = () => {
    try {
      setIsExporting(true);
      
      // Form CSV header
      const headers = ['ID', 'Name', 'Email', 'Role', 'Subscription Status', 'Subscription Type', 'End Date', 'Registration Date', 'Last Login', 'Events Count', 'Trial Status'];
      
      // Form data
      const data = userList.map(user => [
        user.id,
        user.name || '',
        user.email || '',
        user.role || '',
        user.subscription_status || '',
        user.subscription_type || '',
        user.subscription_end ? new Date(user.subscription_end).toLocaleDateString() : '',
        user.created_at ? new Date(user.created_at).toLocaleDateString() : '',
        user.last_login ? new Date(user.last_login).toLocaleDateString() : '',
        getUserEventCount(user.id),
        getTrialStatus(user)
      ]);
      
      // Combine header and data
      const csvContent = [
        headers.join(','),
        ...data.map(row => row.join(','))
      ].join('\n');
      
      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `users_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setIsExporting(false);
    } catch (err) {
      console.error('Error exporting users:', err);
      setError(err.message);
      setIsExporting(false);
    }
  };
  
  // Toggle column visibility settings
  const handleToggleColumn = (columnName) => {
    setVisibleColumns({
      ...visibleColumns,
      [columnName]: !visibleColumns[columnName]
    });
  };
  
  // Send bulk notification
  const handleSendBulkNotification = () => {
    // Here should be API request to send notifications to selected users
    // In reality need to add functionality to select users
    alert('Bulk notification feature will be implemented later');
  };

  // Function to get event count for a user
  const getUserEventCount = (userId) => {
    if (userData.events_by_user && userData.events_by_user[userId]) {
      return userData.events_by_user[userId].event_count;
    }
    return 0; // Return 0 if no data found for this user
  };

  // NEW: Function to get trial status
  const getTrialStatus = (user) => {
    if (!user.trial_ends_at) return 'No trial';
    
    const trialEnd = new Date(user.trial_ends_at);
    const now = new Date();
    
    if (trialEnd > now) {
      const daysLeft = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
      return `Active (${daysLeft} days left)`;
    } else {
      return 'Expired';
    }
  };

  // Function to determine user activity status based on last login date
  const getUserActivityStatus = (user) => {
    if (!user.last_login) return 'Never logged in';
    
    const lastLogin = new Date(user.last_login);
    const now = new Date();
    const diffMs = now - lastLogin;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays < 1) return 'Active today';
    if (diffDays < 7) return 'Active this week';
    if (diffDays < 30) return 'Active this month';
    return 'Inactive for over a month';
  };
  
  // Prepare data for charts based on received data
  const prepareSubscriptionData = () => {
    if (!userData.bySubscriptionType || typeof userData.bySubscriptionType !== 'object') {
      return [];
    }
    return Object.entries(userData.bySubscriptionType).map(([name, value]) => ({
      name,
      value
    }));
  };
  
  // Format last login date for display
  const formatLastLogin = (lastLogin) => {
    if (!lastLogin) return 'Never';
    
    const date = new Date(lastLogin);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Filter users list
  const getFilteredUsers = () => {
    return userList.filter(user => {
      // Search filter (name or email)
      const searchMatch = !filters.search || 
        (user.name && user.name.toLowerCase().includes(filters.search.toLowerCase())) ||
        (user.email && user.email.toLowerCase().includes(filters.search.toLowerCase()));
      
      // Role filter
      const roleMatch = !filters.role || user.role === filters.role;
      
      // Subscription status filter
      const statusMatch = !filters.subscriptionStatus || user.subscription_status === filters.subscriptionStatus;
      
      // Subscription type filter
      const typeMatch = !filters.subscriptionType || user.subscription_type === filters.subscriptionType;
      
      // Registration date filter
      let dateMatch = true;
      if (filters.dateRange !== 'all' && user.created_at) {
        const createdDate = new Date(user.created_at);
        const now = new Date();
        const diffDays = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));
        
        switch (filters.dateRange) {
          case 'today':
            dateMatch = diffDays < 1;
            break;
          case 'week':
            dateMatch = diffDays < 7;
            break;
          case 'month':
            dateMatch = diffDays < 30;
            break;
          default:
            dateMatch = true;
        }
      }
      
      return searchMatch && roleMatch && statusMatch && typeMatch && dateMatch;
    });
  };

  // If user has no admin rights, show message
  if (!isAuthenticated || (user && user.role !== 'admin')) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-red-600">
          {!isAuthenticated 
            ? "Please log in to access the admin panel" 
            : "You do not have permission to access the admin panel"}
        </div>
      </div>
    );
  }

  // If error occurred, show message
  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-red-600">Error: {error}</div>
      </div>
    );
  }

  // If data is loading, show loading indicator
  if ((usersLoading || statsLoading) && userList.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xl text-gray-600">Loading data...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      {/* User statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded shadow">
          <h3 className="text-gray-500 text-sm">Total Users</h3>
          <p className="text-2xl font-bold">{userData.totalUsers}</p>
        </div>
        <div className="bg-white p-4 rounded shadow">
          <h3 className="text-gray-500 text-sm">Active Subscriptions</h3>
          <p className="text-2xl font-bold">{userData.activeSubscriptions}</p>
        </div>
        <div className="bg-white p-4 rounded shadow">
          <h3 className="text-gray-500 text-sm">Activation Rate</h3>
          <p className="text-2xl font-bold">
            {userData.totalUsers > 0 
              ? Math.round((userData.activeSubscriptions / userData.totalUsers) * 100) 
              : 0}%
          </p>
        </div>
        <div className="bg-white p-4 rounded shadow">
          <h3 className="text-gray-500 text-sm">Conversion (active/total)</h3>
          <p className="text-2xl font-bold">
            {userData.totalUsers > 0 
              ? Math.round((userData.activeSubscriptions / userData.totalUsers) * 100) 
              : 0}%
          </p>
          <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
            <div 
              className="bg-blue-600 h-2.5 rounded-full" 
              style={{ width: `${Math.round((userData.activeSubscriptions / userData.totalUsers) * 100)}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Charts and diagrams */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {/* User growth chart */}
        <div className="bg-white p-4 rounded shadow">
          <h3 className="font-bold mb-4">User Growth</h3>
          <div className="h-64">
            {(() => {
              try {
                return (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={userData.monthlyGrowth.length > 0 ? userData.monthlyGrowth : []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="count" 
                        stroke="#8884d8" 
                        name="Number of users" 
                      />
                    </LineChart>
                  </ResponsiveContainer>
                );
              } catch (error) {
                console.error('Error displaying growth chart:', error);
                return <div className="text-center py-4 text-red-500">Error displaying chart</div>;
              }
            })()}
          </div>
        </div>
        
        {/* Subscription distribution */}
        <div className="bg-white p-4 rounded shadow">
          <h3 className="font-bold mb-4">Subscriptions by Type</h3>
          <div className="h-64">
            {(() => {
              try {
                return (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={prepareSubscriptionData()}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {prepareSubscriptionData().map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                );
              } catch (error) {
                console.error('Error displaying subscription chart:', error);
                return <div className="text-center py-4 text-red-500">Error displaying chart</div>;
              }
            })()}
          </div>
        </div>
        
        {/* Region distribution (using placeholder for now) */}
        <div className="bg-white p-4 rounded shadow">
          <h3 className="font-bold mb-4">Users by Region</h3>
          <div className="h-64">
            {(() => {
              try {
                return (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={REGION_DATA}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" fill="#8884d8" name="Count" />
                    </BarChart>
                  </ResponsiveContainer>
                );
              } catch (error) {
                console.error('Error displaying region chart:', error);
                return <div className="text-center py-4 text-red-500">Error displaying chart</div>;
              }
            })()}
          </div>
        </div>
      </div>

      {/* Tools and filters panel */}
      <div className="bg-white p-4 rounded shadow mb-6">
        <div className="flex flex-wrap justify-between items-center mb-4">
          <h3 className="font-bold">Users List</h3>
          
          <div className="flex space-x-2">
            <button 
              className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
              onClick={handleSendBulkNotification}
            >
              Send Notification
            </button>
            <button 
              className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
              onClick={handleExportCSV}
              disabled={isExporting}
            >
              {isExporting ? 'Exporting...' : 'Export CSV'}
            </button>
            <button 
              className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
              onClick={() => setIsColumnSettingsOpen(!isColumnSettingsOpen)}
            >
              Column Settings
            </button>
          </div>
        </div>
        
        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
          {/* Search */}
          <div>
            <label className="block text-sm text-gray-500 mb-1">Search</label>
            <input 
              type="text" 
              className="w-full px-3 py-2 border rounded"
              placeholder="Name or email"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>
          
          {/* Role filter */}
          <div>
            <label className="block text-sm text-gray-500 mb-1">Role</label>
            <select 
              className="w-full px-3 py-2 border rounded"
              value={filters.role}
              onChange={(e) => handleFilterChange('role', e.target.value)}
            >
              <option value="">All roles</option>
              <option value="admin">Administrator</option>
              <option value="organizer">Organizer</option>
              <option value="worker">Staff</option>
            </select>
          </div>
          
          {/* Subscription status filter */}
          <div>
            <label className="block text-sm text-gray-500 mb-1">Subscription Status</label>
            <select 
              className="w-full px-3 py-2 border rounded"
              value={filters.subscriptionStatus}
              onChange={(e) => handleFilterChange('subscriptionStatus', e.target.value)}
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="expired">Expired</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          
          {/* Subscription type filter */}
          <div>
            <label className="block text-sm text-gray-500 mb-1">Subscription Type</label>
            <select 
              className="w-full px-3 py-2 border rounded"
              value={filters.subscriptionType}
              onChange={(e) => handleFilterChange('subscriptionType', e.target.value)}
            >
              <option value="">All types</option>
              <option value="free">Free</option>
              <option value="basic">Basic</option>
              <option value="pro">Pro</option>
            </select>
          </div>
          
          {/* Registration date filter */}
          <div>
            <label className="block text-sm text-gray-500 mb-1">Registration Date</label>
            <select 
              className="w-full px-3 py-2 border rounded"
              value={filters.dateRange}
              onChange={(e) => handleFilterChange('dateRange', e.target.value)}
            >
              <option value="all">All time</option>
              <option value="today">Today</option>
              <option value="week">This week</option>
              <option value="month">This month</option>
            </select>
          </div>
        </div>
        
        {/* Column settings (popup) */}
        {isColumnSettingsOpen && (
          <div 
            ref={columnSettingsRef}
            className="bg-white border rounded shadow-lg p-4 absolute right-8 z-10"
          >
            <h4 className="font-bold mb-2">Column Display Settings</h4>
            <div className="space-y-2">
              <div className="flex items-center">
                <input 
                  type="checkbox" 
                  id="col-id" 
                  checked={visibleColumns.id} 
                  onChange={() => handleToggleColumn('id')}
                  className="mr-2"
                />
                <label htmlFor="col-id">ID</label>
              </div>
              <div className="flex items-center">
                <input 
                  type="checkbox" 
                  id="col-name" 
                  checked={visibleColumns.name} 
                  onChange={() => handleToggleColumn('name')}
                  className="mr-2"
                />
                <label htmlFor="col-name">Name</label>
              </div>
              <div className="flex items-center">
                <input 
                  type="checkbox" 
                  id="col-email" 
                  checked={visibleColumns.email} 
                  onChange={() => handleToggleColumn('email')}
                  className="mr-2"
                />
                <label htmlFor="col-email">Email</label>
              </div>
              <div className="flex items-center">
                <input 
                  type="checkbox" 
                  id="col-role" 
                  checked={visibleColumns.role} 
                  onChange={() => handleToggleColumn('role')}
                  className="mr-2"
                />
                <label htmlFor="col-role">Role</label>
              </div>
              <div className="flex items-center">
                <input 
                  type="checkbox" 
                  id="col-status" 
                  checked={visibleColumns.subscriptionStatus} 
                  onChange={() => handleToggleColumn('subscriptionStatus')}
                  className="mr-2"
                />
                <label htmlFor="col-status">Subscription Status</label>
              </div>
              <div className="flex items-center">
                <input 
                  type="checkbox" 
                  id="col-type" 
                  checked={visibleColumns.subscriptionType} 
                  onChange={() => handleToggleColumn('subscriptionType')}
                  className="mr-2"
                />
                <label htmlFor="col-type">Subscription Type</label>
              </div>
              <div className="flex items-center">
                <input 
                  type="checkbox" 
                  id="col-end" 
                  checked={visibleColumns.subscriptionEnd} 
                  onChange={() => handleToggleColumn('subscriptionEnd')}
                  className="mr-2"
                />
                <label htmlFor="col-end">End Date</label>
              </div>
              <div className="flex items-center">
                <input 
                  type="checkbox" 
                  id="col-trial" 
                  checked={visibleColumns.trialStatus} 
                  onChange={() => handleToggleColumn('trialStatus')}
                  className="mr-2"
                />
                <label htmlFor="col-trial">Trial Status</label>
              </div>
              <div className="flex items-center">
                <input 
                  type="checkbox" 
                  id="col-created" 
                  checked={visibleColumns.createdAt} 
                  onChange={() => handleToggleColumn('createdAt')}
                  className="mr-2"
                />
                <label htmlFor="col-created">Registration Date</label>
              </div>
              <div className="flex items-center">
                <input 
                  type="checkbox" 
                  id="col-lastLogin" 
                  checked={visibleColumns.lastLogin} 
                  onChange={() => handleToggleColumn('lastLogin')}
                  className="mr-2"
                />
                <label htmlFor="col-lastLogin">Last Login</label>
              </div>
              <div className="flex items-center">
                <input 
                  type="checkbox" 
                  id="col-events" 
                  checked={visibleColumns.eventsCount} 
                  onChange={() => handleToggleColumn('eventsCount')}
                  className="mr-2"
                />
                <label htmlFor="col-events">Events Count</label>
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <button 
                className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
                onClick={() => setIsColumnSettingsOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}
        
        {/* Users table */}
        {usersLoading ? (
          <div className="text-center py-4">Loading...</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead>
                  <tr>
                    {visibleColumns.id && <th className="py-2 px-4 border-b text-left">ID</th>}
                    {visibleColumns.name && <th className="py-2 px-4 border-b text-left">Name</th>}
                    {visibleColumns.email && <th className="py-2 px-4 border-b text-left">Email</th>}
                    {visibleColumns.role && <th className="py-2 px-4 border-b text-left">Role</th>}
                    {visibleColumns.subscriptionStatus && <th className="py-2 px-4 border-b text-left">Subscription Status</th>}
                    {visibleColumns.subscriptionType && <th className="py-2 px-4 border-b text-left">Subscription Type</th>}
                    {visibleColumns.subscriptionEnd && <th className="py-2 px-4 border-b text-left">End Date</th>}
                    {visibleColumns.trialStatus && <th className="py-2 px-4 border-b text-left">Trial Status</th>}
                    {visibleColumns.createdAt && <th className="py-2 px-4 border-b text-left">Registration Date</th>}
                    {visibleColumns.lastLogin && <th className="py-2 px-4 border-b text-left">Last Login</th>}
                    {visibleColumns.eventsCount && <th className="py-2 px-4 border-b text-left">Events</th>}
                    <th className="py-2 px-4 border-b text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredUsers().map(user => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      {visibleColumns.id && <td className="py-2 px-4 border-b">{user.id}</td>}
                      {visibleColumns.name && <td className="py-2 px-4 border-b">{user.name || '—'}</td>}
                      {visibleColumns.email && <td className="py-2 px-4 border-b">{user.email}</td>}
                      {visibleColumns.role && <td className="py-2 px-4 border-b">{user.role}</td>}
                      {visibleColumns.subscriptionStatus && (
                        <td className="py-2 px-4 border-b">
                          <span className={`px-2 py-1 rounded text-xs ${
                            user.subscription_status === 'active' ? STATUS_COLORS.active : 
                            user.subscription_status === 'inactive' ? STATUS_COLORS.inactive :
                            user.subscription_status === 'expired' ? STATUS_COLORS.expired :
                            STATUS_COLORS.pending
                          }`}>
                            {user.subscription_status || 'not set'}
                          </span>
                        </td>
                      )}
                      {visibleColumns.subscriptionType && <td className="py-2 px-4 border-b">{user.subscription_type || '—'}</td>}
                      {visibleColumns.subscriptionEnd && (
                        <td className="py-2 px-4 border-b">
                          {user.subscription_end 
                            ? new Date(user.subscription_end).toLocaleDateString() 
                            : '—'}
                        </td>
                      )}
                      {visibleColumns.trialStatus && (
                        <td className="py-2 px-4 border-b">
                          <span className={`px-2 py-1 rounded text-xs ${
                            getTrialStatus(user).includes('Active') ? 'bg-blue-100 text-blue-800' :
                            getTrialStatus(user) === 'Expired' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {getTrialStatus(user)}
                          </span>
                        </td>
                      )}
                      {visibleColumns.createdAt && (
                        <td className="py-2 px-4 border-b">
                          {user.created_at 
                            ? new Date(user.created_at).toLocaleDateString() 
                            : '—'}
                        </td>
                      )}
                      {visibleColumns.lastLogin && (
                        <td className="py-2 px-4 border-b">
                          <span className={`text-xs ${
                            getUserActivityStatus(user) === 'Active today' ? 'text-green-600' :
                            getUserActivityStatus(user) === 'Active this week' ? 'text-blue-600' :
                            getUserActivityStatus(user) === 'Active this month' ? 'text-yellow-600' :
                            'text-gray-600'
                          }`}>
                            {formatLastLogin(user.last_login)}
                          </span>
                        </td>
                      )}
                      {visibleColumns.eventsCount && (
                        <td className="py-2 px-4 border-b">
                          <span className={`px-2 py-1 rounded text-xs ${
                            getUserEventCount(user.id) > 5 ? 'bg-green-100 text-green-800' : 
                            getUserEventCount(user.id) > 0 ? 'bg-blue-100 text-blue-800' : 
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {getUserEventCount(user.id)}
                          </span>
                        </td>
                      )}
                      <td className="py-2 px-4 border-b">
                        <div className="flex flex-wrap gap-1">
                          <button 
                            className="text-blue-500 hover:underline text-xs"
                            onClick={() => handleEditClick(user)}
                          >
                            Edit
                          </button>
                          
                          {/* NEW: Trial management buttons */}
                          {!user.trial_ends_at || new Date(user.trial_ends_at) <= new Date() ? (
                            <button 
                              className="text-green-500 hover:underline text-xs"
                              onClick={() => handleTrialClick(user)}
                            >
                              Add Trial
                            </button>
                          ) : (
                            <button 
                              className="text-orange-500 hover:underline text-xs"
                              onClick={() => handleCancelTrial(user.id)}
                            >
                              Cancel Trial
                            </button>
                          )}
                          
                          {/* NEW: Subscription management buttons */}
                          {user.subscription_status !== 'active' ? (
                            <button 
                              className="text-purple-500 hover:underline text-xs"
                              onClick={() => handleSubscriptionClick(user)}
                            >
                              Add Sub
                            </button>
                          ) : (
                            <button 
                              className="text-red-500 hover:underline text-xs"
                              onClick={() => handleCancelSubscription(user.id)}
                            >
                              Cancel Sub
                            </button>
                          )}
                          
                          <button 
                            className="text-red-500 hover:underline text-xs"
                            onClick={() => handleDeleteClick(user)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            <div className="mt-4 flex justify-between items-center">
              <div className="flex gap-2">
                <button
                  className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
                  disabled={pagination.skip === 0}
                  onClick={() => handlePageChange(pagination.skip - pagination.limit)}
                >
                  Back
                </button>
                {/* Page numbers */}
                <div className="flex gap-1">
                  {Array.from({ length: Math.min(5, Math.ceil(pagination.total / pagination.limit)) }, (_, i) => {
                    const currentPage = Math.floor(pagination.skip / pagination.limit);
                    let pageNum = i;
                    
                    if (currentPage > 2) {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    if (pageNum >= Math.ceil(pagination.total / pagination.limit)) {
                      return null;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        className={`px-3 py-1 rounded ${
                          currentPage === pageNum ? 'bg-blue-500 text-white' : 'bg-gray-200'
                        }`}
                        onClick={() => handlePageChange(pageNum * pagination.limit)}
                      >
                        {pageNum + 1}
                      </button>
                    );
                  })}
                </div>
                <button
                  className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
                  disabled={pagination.skip + pagination.limit >= pagination.total}
                  onClick={() => handlePageChange(pagination.skip + pagination.limit)}
                >
                  Next
                </button>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-gray-600">
                  Showing {Math.min(pagination.limit, getFilteredUsers().length)} of {pagination.total} users
                </span>
                <select
                  className="px-2 py-1 border rounded"
                  value={pagination.limit}
                  onChange={(e) => setPagination({...pagination, limit: Number(e.target.value), skip: 0})}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* User edit modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div 
            ref={editModalRef}
            className="bg-white rounded-lg p-6 w-full max-w-lg"
          >
            <h2 className="text-xl font-bold mb-4">Edit User</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-500 mb-1">Name</label>
                <input 
                  type="text" 
                  name="name"
                  className="w-full px-3 py-2 border rounded"
                  value={editForm.name}
                  onChange={handleEditFormChange}
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-500 mb-1">Email</label>
                <input 
                  type="email" 
                  name="email"
                  className="w-full px-3 py-2 border rounded"
                  value={editForm.email}
                  onChange={handleEditFormChange}
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-500 mb-1">Role</label>
                <select 
                  name="role"
                  className="w-full px-3 py-2 border rounded"
                  value={editForm.role}
                  onChange={handleEditFormChange}
                >
                  <option value="admin">Administrator</option>
                  <option value="organizer">Organizer</option>
                  <option value="worker">Staff</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-gray-500 mb-1">Subscription Status</label>
                <select 
                  name="subscriptionStatus"
                  className="w-full px-3 py-2 border rounded"
                  value={editForm.subscriptionStatus}
                  onChange={handleEditFormChange}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="expired">Expired</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-gray-500 mb-1">Subscription Type</label>
                <select 
                  name="subscriptionType"
                  className="w-full px-3 py-2 border rounded"
                  value={editForm.subscriptionType}
                  onChange={handleEditFormChange}
                >
                  <option value="free">Free</option>
                  <option value="basic">Basic</option>
                  <option value="pro">Pro</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-gray-500 mb-1">Subscription End Date</label>
                <input 
                  type="date" 
                  name="subscriptionEnd"
                  className="w-full px-3 py-2 border rounded"
                  value={editForm.subscriptionEnd}
                  onChange={handleEditFormChange}
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 mt-6">
              <button 
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                onClick={handleCloseEditModal}
              >
                Cancel
              </button>
              <button 
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                onClick={handleSaveUser}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NEW: Trial activation modal */}
      {isTrialModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div 
            ref={trialModalRef}
            className="bg-white rounded-lg p-6 w-full max-w-md"
          >
            <h2 className="text-xl font-bold mb-4">Activate Trial</h2>
            
            <p className="mb-4">
              Activate trial for user: <span className="font-bold">{userForTrial?.name || userForTrial?.email}</span>
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-500 mb-1">Trial Duration (days)</label>
                <input 
                  type="number" 
                  name="days"
                  className="w-full px-3 py-2 border rounded"
                  value={trialForm.days}
                  onChange={handleTrialFormChange}
                  min="1"
                  max="365"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 mt-6">
              <button 
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                onClick={handleCloseTrialModal}
              >
                Cancel
              </button>
              <button 
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                onClick={handleActivateTrial}
                disabled={isSaving}
              >
                {isSaving ? 'Activating...' : 'Activate Trial'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NEW: Subscription activation modal */}
      {isSubscriptionModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div 
            ref={subscriptionModalRef}
            className="bg-white rounded-lg p-6 w-full max-w-md"
          >
            <h2 className="text-xl font-bold mb-4">Activate Subscription</h2>
            
            <p className="mb-4">
              Activate subscription for user: <span className="font-bold">{userForSubscription?.name || userForSubscription?.email}</span>
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-500 mb-1">Subscription Type</label>
                <select 
                  name="subscriptionType"
                  className="w-full px-3 py-2 border rounded"
                  value={subscriptionForm.subscriptionType}
                  onChange={handleSubscriptionFormChange}
                >
                  <option value="basic">Basic</option>
                  <option value="pro">Pro</option>
                  <option value="premium">Premium</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-gray-500 mb-1">Duration (days)</label>
                <input 
                  type="number" 
                  name="days"
                  className="w-full px-3 py-2 border rounded"
                  value={subscriptionForm.days}
                  onChange={handleSubscriptionFormChange}
                  min="1"
                  max="3650"
                />
              </div>
              
              <div className="text-center text-gray-500">or</div>
              
              <div>
                <label className="block text-sm text-gray-500 mb-1">Specific End Date</label>
                <input 
                  type="date" 
                  name="endDate"
                  className="w-full px-3 py-2 border rounded"
                  value={subscriptionForm.endDate}
                  onChange={handleSubscriptionFormChange}
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 mt-6">
              <button 
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                onClick={handleCloseSubscriptionModal}
              >
                Cancel
              </button>
              <button 
                className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600"
                onClick={handleActivateSubscription}
                disabled={isSaving}
              >
                {isSaving ? 'Activating...' : 'Activate Subscription'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete confirmation modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div 
            ref={deleteModalRef}
            className="bg-white rounded-lg p-6 w-full max-w-md"
          >
            <h2 className="text-xl font-bold mb-4">Delete Confirmation</h2>
            
            <p className="mb-6">
              Are you sure you want to delete user <span className="font-bold">{userToDelete?.name || userToDelete?.email}</span>?
              This action cannot be undone.
            </p>
            
            <div className="flex justify-end space-x-2">
              <button 
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                onClick={handleCloseDeleteModal}
              >
                Cancel
              </button>
              <button 
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                onClick={handleConfirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersTab;
